
/* ==========================================
   YAABU CART DRAWER
   With automatic header cart count updates
   ========================================== */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code === 'Escape') this.close();
    });

    this.querySelector('#CartDrawer-Overlay')
      ?.addEventListener('click', this.close.bind(this));

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote =
      this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    this.querySelector('cart-drawer-items')
      ?.dispatchViewEvent();

    window.YaabuRefreshCartCount?.();
  }

  close() {
    this.classList.remove('active');

    removeTrapFocus(this.activeElement);

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open')
      );
    });

    cartDrawerNote.parentElement.addEventListener(
      'keyup',
      onKeyUpEscape
    );
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner?.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections?.[section.id];

      if (!sectionElement || !sectionHTML) return;

      const newContent = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );

      if (newContent !== null) {
        sectionElement.innerHTML = newContent;
      }
    });

    window.YaabuRefreshCartCount?.();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')
        ?.addEventListener('click', this.close.bind(this));

      this.open();

      window.YaabuRefreshCartCount?.();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    return doc.querySelector(selector)?.innerHTML ?? null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);


/* ==========================================
   YAABU CART DRAWER ITEMS
   ========================================== */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


/* ==========================================
   YAABU LIVE HEADER CART COUNT SYNC
   ========================================== */

(function () {
  if (window.YaabuCartCountSyncInstalled) return;

  window.YaabuCartCountSyncInstalled = true;

  let refreshSequence = 0;

  async function refreshCartCount() {
    const sequence = ++refreshSequence;

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const response = await fetch(
        root + 'cart.js',
        {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          }
        }
      );

      if (!response.ok) return;

      const cart = await response.json();

      // Ignore responses superseded by a newer refresh.
      if (sequence !== refreshSequence) return;

      const count = Number(cart.item_count) || 0;

      updateHeaderBadge(count);
    } catch (error) {
      console.warn(
        'Yaabu cart count refresh failed:',
        error
      );
    }
  }

  function updateHeaderBadge(count) {
    const cartIcon =
      document.querySelector('#cart-icon-bubble');

    if (!cartIcon) return;

    let badge = cartIcon.querySelector(
      '.cart-count-bubble'
    );

    if (count <= 0) {
      if (badge) badge.remove();

      cartIcon.setAttribute(
        'aria-label',
        'Cart, 0 items'
      );

      return;
    }

    if (!badge) {
      badge = document.createElement('div');

      badge.className = 'cart-count-bubble';

      cartIcon.appendChild(badge);
    }

    let countText = badge.querySelector('span');

    if (!countText) {
      countText = document.createElement('span');

      badge.appendChild(countText);
    }

    countText.textContent = String(count);

    // Update accessibility-only count too, if present.
    const hiddenCount = badge.querySelector(
      '.visually-hidden'
    );

    if (hiddenCount) {
      hiddenCount.textContent =
        `${count} items`;
    }

    badge.style.display = '';
    badge.style.visibility = 'visible';

    cartIcon.setAttribute(
      'aria-label',
      `Cart, ${count} items`
    );
  }

  window.YaabuRefreshCartCount =
    refreshCartCount;

  // Detect cart changes made through fetch().
  const originalFetch = window.fetch;

  window.fetch = function (...args) {
    const request = args[0];

    const url =
      typeof request === 'string'
        ? request
        : request?.url || '';

    const isCartMutation =
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/.test(
        String(url)
      );

    const result = originalFetch.apply(this, args);

    if (isCartMutation) {
      result.then(
        (response) => {
          if (response.ok) {
            setTimeout(refreshCartCount, 100);
            setTimeout(refreshCartCount, 500);
          }
        },
        () => {}
      );
    }

    return result;
  };

  // Detect cart changes made through XMLHttpRequest.
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...args
  ) {
    this._yaabuCartMutation =
      /\/cart\/(add|change|update|clear)(?:\.js)?(?:[?#]|$)/.test(
        String(url)
      );

    return originalOpen.call(
      this,
      method,
      url,
      ...args
    );
  };

  XMLHttpRequest.prototype.send = function (...args) {
    if (this._yaabuCartMutation) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) {
          setTimeout(refreshCartCount, 100);
          setTimeout(refreshCartCount, 500);
        }
      });
    }

    return originalSend.apply(this, args);
  };

  // Refresh after theme cart events.
  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'cart:added',
    'cart:rendered'
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      () => setTimeout(refreshCartCount, 100)
    );
  });

  // Refresh when returning to the page.
  window.addEventListener(
    'pageshow',
    refreshCartCount
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) refreshCartCount();
    }
  );

  // Fallback for cart actions from custom sections.
  setInterval(() => {
    if (!document.hidden) {
      refreshCartCount();
    }
  }, 2000);

  // Initial count.
  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      refreshCartCount,
      { once: true }
    );
  } else {
    refreshCartCount();
  }
})();
